/**
 * "This person works fewer days than the company — is their allowance right?"
 *
 * ⚠️ ROB FOUND THIS ON HIS OWN TEST DATA, 13/08/2026. He set someone to work Mondays only
 * and they kept the company default of **20 days** — for a job of about 52 days a year.
 * Nothing anywhere said a word. HolidayHive has never pro-rated the base allowance by
 * working pattern (the pro-rata it does is for mid-year STARTERS), and the statutory check
 * only ever looks at the floor, so it happily reported everyone as compliant.
 *
 * His instinct on the fix was right: *"it needs to say something when they are adding the
 * employee or changing their hours / days they work."* The moment the pattern is set is the
 * moment somebody can still think about it.
 *
 * ⚠️ IT SUGGESTS, IT NEVER APPLIES. Same rule as the working-week suggestion: an answer
 * nobody had to type is the one nobody checks. And the admin gets a third option — their
 * own number — because Rob pointed out the obvious: *"they might think well, he has got 5
 * bank holidays, he can't have any more."* Our arithmetic is a starting point, not a verdict.
 *
 * ⚠️ NO LEGAL CLAIMS IN HERE. Statutory floors live in ONE place (`services/jurisdictions`,
 * surfaced on the Settings advisory and the Perfection Box) and we only model a few
 * countries. Rob, on doing it per-country: *"the rest of the world we don't know and we
 * don't want to know as that has headache written all over it."* Everything below is
 * proportion — pure arithmetic, true in every country.
 *
 * Pure. Checked by `node src/lib/proRata.checks.mjs`, and pinned against the Python twin
 * in `backend/services/pro_rata.py` by `backend/tests/test_pro_rata_agrees.py`.
 */

/** Round to the nearest half day — the same granularity the mid-year pro-rata uses. */
export const roundHalfDay = (days) => Math.round(days * 2) / 2;

const WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * How many days a week a working pattern has any work in. Pure.
 *
 * ⚠️ BOTH SHAPES (Rob's PC, 22/10/2026). A pattern is either sessions
 * (`{monday: {am: {enabled}, pm: {enabled}}}`) or the older flat shape
 * (`{monday: {enabled, start, end}}`) — and company hours are often still flat. Counting
 * sessions only saw ZERO days in a flat company week, so the "works a full week on a
 * part-time allowance" box silently never appeared — and anyone inheriting that week
 * would have read as working no days at all. The backend twin is
 * `services/schedule.working_days`, which reads both.
 */
export const daysAWeekIn = (week) => {
  let w = week;
  if (typeof w === 'string') {
    try { w = JSON.parse(w); } catch (e) { return null; }
  }
  if (!w || typeof w !== 'object') return null;
  const n = WEEK.filter((d) => {
    const day = w[d];
    if (!day || typeof day !== 'object') return false;
    if ('am' in day || 'pm' in day) return !!(day.am?.enabled || day.pm?.enabled);
    return !!day.enabled;
  }).length;
  return n || null;
};

/**
 * The allowance that matches the days they actually work. `null` when we cannot say.
 *
 * ⚠️ `null` IS A REAL ANSWER. A company with no working week recorded, or a person whose
 * pattern hasn't been set, must produce silence — not a confident zero.
 */
// ⚠️ BY DAYS A WEEK, NOT HOURS (22/10/2026). An allowance is counted in the person's OWN
// days, so the proportion is their days against the company's. Hours against hours offered
// John (five short days) 13 days — below the legal floor the next box then warned about.
// See the Python twin, `backend/services/pro_rata.py`.
export const suggestedAllowance = (companyDefaultDays, theirDaysAWeek, companyDaysAWeek) => {
  if (!companyDaysAWeek || companyDaysAWeek <= 0) return null;
  if (theirDaysAWeek === null || theirDaysAWeek === undefined) return null;
  if (companyDefaultDays === null || companyDefaultDays === undefined) return null;
  return roundHalfDay(companyDefaultDays * (theirDaysAWeek / companyDaysAWeek));
};

/**
 * Is this allowance worth querying? Returns `null`, or `{ direction, suggested }`.
 *
 * ⚠️ TWO DIRECTIONS, AND ROB ASKED FOR THE SECOND ONE — *"Yes agreed, it should work the
 * other way to"*. The second is the one that costs the EMPLOYEE rather than the employer,
 * so if anything it matters more.
 *
 *   'over'  — they work less than a full week and still hold the COMPANY DEFAULT, exactly.
 *             ⚠️ Exactly, on purpose: Rob's call. An admin who deliberately typed 12 days
 *             for a part-timer has thought about it, and nagging them teaches people to
 *             ignore warnings.
 *
 *   'under' — they work a full week (or more) but hold LESS than the company default,
 *             which is what a part-time figure looks like after somebody's hours went up.
 */
export const allowanceNeedsAttention = ({
  allowanceDays, companyDefaultDays, theirDaysAWeek, companyDaysAWeek,
}) => {
  const suggested = suggestedAllowance(
    companyDefaultDays, theirDaysAWeek, companyDaysAWeek);
  if (suggested === null || allowanceDays === null || allowanceDays === undefined) {
    return null;
  }
  const fraction = theirDaysAWeek / companyDaysAWeek;

  if (fraction < 1 && allowanceDays === companyDefaultDays) {
    return { direction: 'over', suggested };
  }
  // ⚠️ `> 0.999` rather than `>= 1`: hours are stored in minutes and a 37.5-hour week
  // divided out can land a hair under. A rounding artefact must not silence a real warning.
  if (fraction > 0.999 && allowanceDays < companyDefaultDays) {
    return { direction: 'under', suggested };
  }
  return null;
};

/**
 * How many days a week, for the sentence "X works 1 day a week, the company works 5".
 * Minutes in, days out, to one decimal — 2.5 days a week is a real pattern.
 */
export const daysAWeek = (weeklyMinutes, dayMinutes) => {
  if (!weeklyMinutes || !dayMinutes || dayMinutes <= 0) return null;
  return Math.round((weeklyMinutes / dayMinutes) * 10) / 10;
};
